import axios from "axios";

// Request coalescing + short-TTL cache for idempotent READ endpoints.
//
// Many components independently fetch the same global data (brand/getfunnel,
// profile, leaderboard, groups, banners, notifications). On a single page load
// they all fire at once — and React StrictMode doubles every effect in dev — so
// the network tab fills with duplicate calls. Keyed by URL + body + auth token,
// this collapses concurrent identical requests into ONE network call (in-flight
// coalescing) and serves a brief cache so quick re-mounts don't refetch.
//
// Use ONLY for reads. Never route mutations through this. After a mutation that
// changes cached data, call bustCache(urlSubstring) so the next read refetches
// (or pass { force: true } on a read that must be fresh).

const inflight = new Map(); // key -> pending promise
const cache = new Map();    // key -> { t, res }

function keyOf(url, body, auth) {
  const tok = auth?.headers?.Authorization || "";
  return `${url}|${JSON.stringify(body || {})}|${tok}`;
}

/**
 * POST that dedupes/caches. Resolves to the axios response (same shape as
 * axios.post), so callers keep reading res.data unchanged.
 * @param {object} [opts] - { ttl=30000 ms, force=false, auth } (auth = axios config)
 */
export function cachedPost(url, body, { ttl = 30000, force = false, auth } = {}) {
  const key = keyOf(url, body, auth);
  if (!force) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.t < ttl) return Promise.resolve(hit.res);
  }
  // Coalesce even when forced, so a burst of forced reads still makes one call.
  const pending = inflight.get(key);
  if (pending) return pending;

  const p = axios
    .post(url, body, auth)
    .then((res) => {
      cache.set(key, { t: Date.now(), res });
      inflight.delete(key);
      return res;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });
  inflight.set(key, p);
  return p;
}

// Drop cached reads whose key contains `match` (e.g. "/profile") so the next
// read hits the network. Call after a mutation that changes that data.
export function bustCache(match) {
  for (const k of Array.from(cache.keys())) if (k.includes(match)) cache.delete(k);
}
